import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { apiCall, ApiConfig } from '../../../config/api'
import { Loading } from '../../../components/UI'
import { OrderDetails, OrderInfo } from '../../../interfaces/Order/Order'
import { WorkOrderDetail } from '../../../interfaces/WorkOrderOutbound'
import { useCourier } from '../../../hooks/useCourier'
import { Courier } from '../../../interfaces/Courier/Courier'
import { activeBoolToText } from '../../../utils/helpers'

interface OrderDetailViewerModernProps {
  workOrderDetails: WorkOrderDetail[]
}

interface WorkOrderOrderReference {
  orderId: number
  referenceId: string
}

const getUniqueOrders = (
  workOrderDetails: WorkOrderDetail[]
): WorkOrderOrderReference[] => {
  const orderMap = new Map<string, WorkOrderOrderReference>()

  workOrderDetails.forEach(({ orderId, referenceId }) => {
    const key = `${orderId}-${referenceId}`

    if (!orderMap.has(key)) {
      orderMap.set(key, { orderId, referenceId })
    }
  })

  return Array.from(orderMap.values())
}

const getCourierNameById = (
  couriers: Courier[] | undefined,
  courierId?: number
) => {
  if (!couriers || !courierId) return 'Sin courier'

  return (
    couriers.find((courier) => courier.courierId === courierId)?.name ||
    'Sin courier'
  )
}

const getCourierServiceNameById = (
  couriers: Courier[] | undefined,
  courierServiceId?: number
) => {
  if (!couriers || !courierServiceId) return 'Sin servicio'

  for (const courier of couriers) {
    const service = courier.courierServices?.find(
      (item) => item.courierServiceId === courierServiceId
    )

    if (service) {
      return service.name
    }
  }

  return 'Sin servicio'
}

const getBrandSummary = (orderInfo?: OrderInfo) => {
  if (!orderInfo) return 'Pendiente'

  const brands = Array.from(
    new Set(
      orderInfo.orderDetails
        .map((detail) => detail.sku.productOrigin?.name?.trim())
        .filter((brand): brand is string => Boolean(brand))
    )
  ).sort((brandA, brandB) => brandA.localeCompare(brandB))

  if (brands.length === 0) return 'Sin marca'
  if (brands.length <= 2) return brands.join(', ')

  return `${brands[0]}, ${brands[1]} +${brands.length - 2}`
}

const getSortedOrderRows = (orderInfo?: OrderInfo) => {
  if (!orderInfo) return []

  return [...orderInfo.orderDetails].sort((detailA, detailB) =>
    detailA.sku.name.localeCompare(detailB.sku.name)
  )
}

const renderMetaChip = (label: string, value: string) => (
  <Chip
    label={`${label}: ${value}`}
    size='small'
    variant='outlined'
    sx={{
      borderRadius: 1.5,
      maxWidth: '100%',
      '& .MuiChip-label': {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    }}
  />
)

export const OrderDetailViewerModern: React.FC<OrderDetailViewerModernProps> = ({
  workOrderDetails
}) => {
  const orders = useMemo(
    () => getUniqueOrders(workOrderDetails),
    [workOrderDetails]
  )
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null)
  const [orderDetailsById, setOrderDetailsById] = useState<Record<number, OrderInfo>>({})
  const [loadingById, setLoadingById] = useState<Record<number, boolean>>({})
  const [errorById, setErrorById] = useState<Record<number, string>>({})

  const {
    couriersState: { couriers },
    getCouriers
  } = useCourier()

  useEffect(() => {
    if (!couriers || couriers.length === 0) {
      getCouriers()
    }
  }, [couriers])

  useEffect(() => {
    if (orders.length === 0) {
      setExpandedOrderId(null)
      return
    }

    setExpandedOrderId((currentExpandedOrderId) => {
      if (orders.length === 1) {
        return orders[0].orderId
      }

      if (
        currentExpandedOrderId !== null &&
        orders.some((order) => order.orderId === currentExpandedOrderId)
      ) {
        return currentExpandedOrderId
      }

      return null
    })
  }, [orders])

  const loadOrderDetails = useCallback(
    async (orderId: number) => {
      if (orderDetailsById[orderId] || loadingById[orderId]) {
        return
      }

      setLoadingById((prev) => ({ ...prev, [orderId]: true }))
      setErrorById((prev) => ({ ...prev, [orderId]: '' }))

      try {
        const response = await apiCall('GET', `${ApiConfig.OrderApi}/${orderId}`)
        const orderInfo = response.data as OrderInfo | undefined

        if (!orderInfo) {
          setErrorById((prev) => ({
            ...prev,
            [orderId]: 'No fue posible cargar el pedido.'
          }))
          return
        }

        setOrderDetailsById((prev) => ({
          ...prev,
          [orderId]: orderInfo
        }))
      } catch (error: any) {
        setErrorById((prev) => ({
          ...prev,
          [orderId]:
            error?.data?.message || 'No fue posible cargar el detalle del pedido.'
        }))
      } finally {
        setLoadingById((prev) => ({ ...prev, [orderId]: false }))
      }
    },
    [loadingById, orderDetailsById]
  )

  useEffect(() => {
    if (expandedOrderId !== null) {
      loadOrderDetails(expandedOrderId)
    }
  }, [expandedOrderId, loadOrderDetails])

  const handleAccordionChange =
    (orderId: number) => (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpandedOrderId(isExpanded ? orderId : null)
    }

  const handleChipClick = (orderId: number) => {
    setExpandedOrderId((currentExpandedOrderId) =>
      currentExpandedOrderId === orderId ? null : orderId
    )
  }

  if (orders.length === 0) {
    return (
      <Paper
        variant='outlined'
        sx={{ p: 2, borderRadius: 2, backgroundColor: 'grey.50' }}
      >
        <Typography color='text.secondary'>
          Esta OT no tiene pedidos asociados.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: 'common.white',
        borderColor: 'divider'
      }}
    >
      <Stack spacing={2}>
        {orders.length > 1 && (
          <Box
            sx={{
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                Pedidos asociados
              </Typography>
              <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
                {orders.map((order) => (
                  <Chip
                    key={`${order.orderId}-${order.referenceId}`}
                    label={order.referenceId}
                    onClick={() => handleChipClick(order.orderId)}
                    color={
                      expandedOrderId === order.orderId ? 'primary' : 'default'
                    }
                    variant={
                      expandedOrderId === order.orderId ? 'filled' : 'outlined'
                    }
                    sx={{ borderRadius: 1.5 }}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        )}

        <Stack spacing={1.5}>
          {orders.map((order) => {
            const orderInfo = orderDetailsById[order.orderId]
            const isLoading = loadingById[order.orderId] === true
            const errorMessage = errorById[order.orderId]
            const rows = getSortedOrderRows(orderInfo)

            return (
              <Accordion
                key={`${order.orderId}-${order.referenceId}`}
                expanded={expandedOrderId === order.orderId}
                onChange={handleAccordionChange(order.orderId)}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor:
                    expandedOrderId === order.orderId ? 'primary.light' : 'divider',
                  borderRadius: '14px !important',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 0.75,
                    backgroundColor:
                      expandedOrderId === order.orderId ? 'rgba(25, 118, 210, 0.06)' : 'grey.50',
                    '& .MuiAccordionSummary-content': {
                      margin: '0 !important'
                    }
                  }}
                >
                  <Stack spacing={0.75} sx={{ width: '100%' }}>
                    <Typography
                      variant='subtitle1'
                      sx={{ fontWeight: 700, lineHeight: 1.2 }}
                    >
                      Pedido {order.referenceId}
                    </Typography>

                    {expandedOrderId === order.orderId && (
                      <Stack
                        direction='row'
                        spacing={0.75}
                        useFlexGap
                        flexWrap='wrap'
                        sx={{ maxWidth: '100%' }}
                      >
                        <Chip
                          label={`ID interno ${order.orderId}`}
                          size='small'
                          variant='outlined'
                          sx={{ borderRadius: 1.5, height: 24 }}
                        />
                        {renderMetaChip('Marca', getBrandSummary(orderInfo))}
                        {renderMetaChip(
                          'Courier',
                          getCourierNameById(couriers, orderInfo?.courierId)
                        )}
                        {renderMetaChip(
                          'Servicio',
                          getCourierServiceNameById(
                            couriers,
                            orderInfo?.courierServiceId
                          )
                        )}
                        {renderMetaChip(
                          'Canal',
                          orderInfo?.origin || 'Pendiente'
                        )}
                      </Stack>
                    )}
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 2, pt: 0 }}>
                  {isLoading && (
                    <Paper
                      variant='outlined'
                      sx={{ p: 3, borderRadius: 2, backgroundColor: 'common.white' }}
                    >
                      <Loading />
                    </Paper>
                  )}

                  {!isLoading && errorMessage && (
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#fff5f5',
                        borderColor: '#fecaca'
                      }}
                    >
                      <Typography color='error.main'>{errorMessage}</Typography>
                    </Paper>
                  )}

                  {!isLoading && !errorMessage && orderInfo && rows.length === 0 && (
                    <Paper
                      variant='outlined'
                      sx={{ p: 2, borderRadius: 2, backgroundColor: 'grey.50' }}
                    >
                      <Typography color='text.secondary'>
                        Esta orden no tiene detalle.
                      </Typography>
                    </Paper>
                  )}

                  {!isLoading && !errorMessage && rows.length > 0 && (
                    <Paper
                      variant='outlined'
                      sx={{ borderRadius: 2, overflow: 'hidden' }}
                    >
                      <TableContainer sx={{ maxHeight: 320, overflow: 'auto' }}>
                        <Table stickyHeader size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}
                              >
                                SKU / EAN
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}
                              >
                                Descripción
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}
                                align='right'
                              >
                                Cantidad
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}
                                align='center'
                              >
                                Alm. externo
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((detail: OrderDetails) => (
                              <TableRow
                                key={`${order.orderId}-${detail.orderDetailId}`}
                                hover
                              >
                                <TableCell>
                                  {detail.sku.ean13 === detail.sku.sku
                                    ? detail.sku.sku
                                    : `${detail.sku.sku} / ${detail.sku.ean13}`}
                                </TableCell>
                                <TableCell>{detail.sku.name}</TableCell>
                                <TableCell align='right'>
                                  {detail.quantity}
                                </TableCell>
                                <TableCell align='center'>
                                  {activeBoolToText(detail.sku.externalStorage)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Stack>
      </Stack>
    </Paper>
  )
}
